/**
 * Admin V3 — Refund reconciliation panel (RFC-1 RU-9)
 *
 * DISPLAY ONLY. Renders the refund / avoir / correction-chain state for a booking,
 * consuming the read-only backend view:
 *   GET /admin/billing/bookings/{id}/refund-view
 *     → { refund_state, credited_total, remaining_creditable, fully_credited,
 *         avoirs[], payment_refunded_ledger[], reconciliation{verdict, categories,
 *         block_reasons, has_refund, repairable, blocked} }
 *
 * The panel NEVER infers financial state on the frontend — every value shown is
 * produced by the backend (RU-6 derived refund_state, the correction chain, the
 * PAYMENT_REFUNDED ledger, and the RU-7 reconciliation verdict). It has NO action
 * buttons: no repair/apply, no refund execution — those are out of scope for RU-9.
 * The existing refund + refund-history affordances on the booking page are untouched.
 */
import React from 'react';
import { Wallet, AlertTriangle } from 'lucide-react';
import { cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';

const REFUND_STATE_PILL = {
  fully_refunded: 'bg-rose-100 text-rose-700 border border-rose-200',
  partially_refunded: 'bg-amber-100 text-amber-700 border border-amber-200',
  not_refunded: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const VERDICT_PILL = {
  reconciled: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  repairable: 'bg-amber-100 text-amber-700 border border-amber-200',
  blocked: 'bg-rose-100 text-rose-700 border border-rose-200',
  no_refund: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const fmtMoney = (v) => (v == null ? '—' : `${Number(v).toFixed(2)} €`);

const fmtDate = (iso) => {
  const s = String(iso || '');
  return s.length >= 10 && s[4] === '-' && s[7] === '-'
    ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`
    : '';
};

export default function RefundReconciliationPanel({ bookingId, locale = DEFAULT_LOCALE }) {
  const { data, loading } = useAdminQuery(
    bookingId ? `/admin/billing/bookings/${bookingId}/refund-view` : null,
  );
  if (loading || !data) return null;

  const {
    refund_state: refundState,
    credited_total: creditedTotal,
    remaining_creditable: remaining,
    avoirs = [],
    payment_refunded_ledger: refundLedger = [],
    reconciliation = {},
  } = data;

  // Render only when there is something to show — a refund, linked avoirs/ledger,
  // or a blocked/inconsistent historical case. A clean never-refunded booking
  // renders nothing (no noise).
  const hasContent =
    reconciliation.has_refund ||
    reconciliation.blocked ||
    avoirs.length > 0 ||
    refundLedger.length > 0;
  if (!hasContent) return null;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="adminv3-refund-reconciliation"
    >
      <div className="mb-3 flex items-center gap-2">
        <Wallet className="h-4 w-4 text-slate-400" />
        <h3 className="text-[13px] font-semibold text-slate-900">
          {t('billing.refundView.title', locale)}
        </h3>
        <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          {t('billing.refundView.readOnly', locale)}
        </span>
      </div>

      {/* Derived refund_state (RU-6) + reconciliation verdict (RU-7) */}
      <div className="flex flex-wrap items-center gap-2">
        {refundState && (
          <span
            data-testid="adminv3-refund-state"
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold',
              REFUND_STATE_PILL[refundState] || REFUND_STATE_PILL.not_refunded,
            )}
          >
            {t(`billing.refundView.state.${refundState}`, locale)}
          </span>
        )}
        {reconciliation.verdict && (
          <span
            data-testid="adminv3-refund-verdict"
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold',
              VERDICT_PILL[reconciliation.verdict] || VERDICT_PILL.no_refund,
            )}
          >
            {t(`billing.refundView.verdict.${reconciliation.verdict}`, locale)}
          </span>
        )}
      </div>

      {/* Blocked / inconsistent historical case — operator warning */}
      {reconciliation.blocked && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50/60 p-2.5"
          data-testid="adminv3-refund-warning"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-600" />
          <div className="text-[12px] text-rose-700">
            <div className="font-semibold">{t('billing.refundView.blockedTitle', locale)}</div>
            {(reconciliation.block_reasons || []).map((r, i) => (
              <div key={i} data-testid="adminv3-refund-block-reason">{r}</div>
            ))}
          </div>
        </div>
      )}

      {/* Backend-computed amounts */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
        <div className="flex justify-between">
          <span className="text-slate-400">{t('billing.refundView.credited', locale)}</span>
          <span className="font-medium" data-testid="adminv3-refund-credited">{fmtMoney(creditedTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">{t('billing.refundView.remaining', locale)}</span>
          <span className="font-medium" data-testid="adminv3-refund-remaining">{fmtMoney(remaining)}</span>
        </div>
      </div>

      {/* Linked avoir(s) — AV number + amount + status */}
      {avoirs.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {t('billing.refundView.avoirs', locale)}
          </div>
          <div className="space-y-1">
            {avoirs.map((a, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-1.5 text-[12px]"
                data-testid="adminv3-refund-avoir-row"
              >
                <span className="font-mono text-slate-700">{a.avoir_number || '—'}</span>
                <span className="text-slate-400">{a.status}</span>
                <span className="font-medium text-slate-900">{fmtMoney(a.total_amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Append-only PAYMENT_REFUNDED ledger row(s) */}
      {refundLedger.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {t('billing.refundView.ledger', locale)}
          </div>
          <div className="space-y-1">
            {refundLedger.map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-2.5 py-1.5 text-[12px]"
                data-testid="adminv3-refund-ledger-row"
              >
                <span className="font-mono text-[11px] text-slate-500">
                  {r.refund_id || r.webhook_event_id || '—'}
                </span>
                <span className="text-slate-400">{fmtDate(r.refund_timestamp || r.created_at)}</span>
                <span className="font-medium text-slate-900">{fmtMoney(r.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
